import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import AxiosInstance from "../services/Api";

// Gender options constant
export const GENDER_OPTIONS = [
  { value: "", label: "Select Gender" },
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "other", label: "Other" },
];

// State types constant
export const STATE_TYPES = {
  FATHER: "father",
  MOTHER: "mother",
  GUARDIAN: "guardian",
};

// Async thunk for fetching Income options
export const fetchIncomeOptions = createAsyncThunk(
  "masterData/fetchIncomeOptions",
  async (_, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.get("/school/getDomain/INC");
      if (response.data?.status) {
        const filteredData = response.data.data
          .filter((item, index) => index !== 0 && item.isActive === 1)
          .map((item) => ({
            value: item.id.toString(),
            label: item.domain_id,
          }));
        return filteredData;
      }
      return [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch income options",
      );
    }
  },
);

// Async thunk for fetching Occupation options
export const fetchOccupationOptions = createAsyncThunk(
  "masterData/fetchOccupationOptions",
  async (_, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.get("/school/getDomain/OCP");
      if (response.data?.status) {
        const filteredData = response.data.data
          .filter((item, index) => index !== 0 && item.isActive === 1)
          .map((item) => ({
            value: item.id.toString(),
            label: item.domain_id,
          }));
        return filteredData;
      }
      return [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch occupation options",
      );
    }
  },
);

// Async thunk for fetching Blood Group options
export const fetchBloodGroupOptions = createAsyncThunk(
  "masterData/fetchBloodGroupOptions",
  async (_, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.get("/school/getDomain/BG");
      if (response.data?.status) {
        const filteredData = response.data.data
          .filter((item, index) => index !== 0 && item.isActive === 1)
          .map((item) => ({
            value: item.id.toString(),
            label: item.domain_id,
          }));
        return filteredData;
      }
      return [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch blood group options",
      );
    }
  },
);

// Async thunk for fetching Category/Caste options
export const fetchCategoryOptions = createAsyncThunk(
  "masterData/fetchCategoryOptions",
  async (_, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.get("/school/getDomain/CA");
      if (response.data?.status) {
        const filteredData = response.data.data
          .filter((item, index) => index !== 0 && item.isActive === 1)
          .map((item) => ({
            value: item.id.toString(),
            label: item.domain_id,
          }));
        return filteredData;
      }
      return [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch category options",
      );
    }
  },
);

// Async thunk for fetching Religion options
export const fetchReligionOptions = createAsyncThunk(
  "masterData/fetchReligionOptions",
  async (_, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.get("/school/getDomain/RELIG");
      if (response.data?.status) {
        const filteredData = response.data.data
          .filter((item, index) => index !== 0 && item.isActive === 1)
          .map((item) => ({
            value: item.id.toString(),
            label: item.domain_id,
          }));
        return filteredData;
      }
      return [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch religion options",
      );
    }
  },
);

// Async thunk for fetching Relation options
export const fetchRelationOptions = createAsyncThunk(
  "masterData/fetchRelationOptions",
  async (_, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.get("/school/getDomain/RELA");
      if (response.data?.status) {
        const filteredData = response.data.data
          .filter((item, index) => index !== 0 && item.isActive === 1)
          .map((item) => ({
            value: item.id.toString(),
            label: item.domain_id,
          }));
        return filteredData;
      }
      return [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch relation options",
      );
    }
  },
);

// Async thunk for fetching Leave Reason options
export const fetchLeaveReasonOptions = createAsyncThunk(
  "masterData/fetchLeaveReasonOptions",
  async (_, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.get("/school/getDomain/LR");
      if (response.data?.status) {
        const filteredData = response.data.data.map((item) => ({
          value: item.domain_id,
          label: item.domain_id,
        }));
        return filteredData;
      }
      return [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch leave reason options",
      );
    }
  },
);

// Async thunk for fetching Academic Year options
// GET /school/academic/:school_id — returns every academic year configured for
// the school. The raw flags (isActive / isUpcoming) are kept on each option so
// callers can default-select the active year.
export const fetchAcademicYearOptions = createAsyncThunk(
  "masterData/fetchAcademicYearOptions",
  async (schoolId, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.get(`/school/academic/${schoolId}`);
      if (response.data?.success || response.data?.status) {
        const filteredData = (response.data.data || []).map((item) => ({
          value: item.academic_year,
          label: item.academic_year,
          id: item.id,
          isActive: item.isActive,
          isUpcoming: item.isUpcoming,
          start_date: item.start_date,
          end_date: item.end_date,
        }));
        return filteredData;
      }
      return [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch academic year options",
      );
    }
  },
);

// Async thunk for fetching Class options
export const fetchClassOptions = createAsyncThunk(
  "masterData/fetchClassOptions",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.post("/school/classList", payload);
      if (response.data?.success || response.data?.status) {
        const filteredData = response.data.data
          .filter((item) => item.domain_id !== "CLASS")
          .map((item) => ({
            value: item.class_id.toString(),
            label: item.domain_id,
          }));
        return filteredData;
      }
      return [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch class options",
      );
    }
  },
);

// Async thunk for fetching Staff Subject options (class dropdown for staff)
export const fetchStaffClassAndSubjects = createAsyncThunk(
  "masterData/fetchStaffClassAndSubjects",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.post(
        "/school/staff/subjects",
        payload,
      );
      if (response.data?.status) {
        return response.data.data || [];
      }
      return [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch staff subjects",
      );
    }
  },
);

// Async thunk for fetching Section options
export const fetchSectionOptions = createAsyncThunk(
  "masterData/fetchSectionOptions",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.post(
        "/school/timeTable/sectionDropdown",
        payload,
      );
      if (response.data?.status) {
        const data = Array.isArray(response.data.data[0])
          ? response.data.data[0]
          : response.data.data;

        const filteredData = data.map((item) => ({
          value: item.section_id,
          label: item.section_id,
        }));
        return filteredData;
      }
      return [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch section options",
      );
    }
  },
);

// Async thunk for fetching Country options
export const fetchCountryOptions = createAsyncThunk(
  "masterData/fetchCountryOptions",
  async (_, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.get("/school/countries");
      if (response.data?.status) {
        const countries = response.data.data.map((country) => ({
          value: country.id.toString(),
          label: country.name,
        }));
        return countries;
      }
      return [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch country options",
      );
    }
  },
);

// Single Async thunk for fetching State options by Country
export const fetchStateOptions = createAsyncThunk(
  "masterData/fetchStateOptions",
  async ({ countryId, stateType }, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.post("/school/states", {
        country_id: parseInt(countryId),
      });
      if (response.data?.status) {
        const states = response.data.data.map((state) => ({
          value: state.id.toString(),
          label: state.name,
        }));
        return { states, stateType };
      }
      return { states: [], stateType };
    } catch (error) {
      return rejectWithValue({
        message:
          error.response?.data?.message || "Failed to fetch state options",
        stateType,
      });
    }
  },
);

const initialState = {
  // Income
  incomeOptions: [],
  incomeLoading: false,
  incomeError: null,

  // Occupation
  occupationOptions: [],
  occupationLoading: false,
  occupationError: null,

  // Blood Group
  bloodGroupOptions: [],
  bloodGroupLoading: false,
  bloodGroupError: null,

  // Category/Caste
  categoryOptions: [],
  categoryLoading: false,
  categoryError: null,

  // Religion
  religionOptions: [],
  religionLoading: false,
  religionError: null,

  // Relation
  relationOptions: [],
  relationLoading: false,
  relationError: null,

  // Leave Reason
  leaveReasonOptions: [],
  leaveReasonLoading: false,
  leaveReasonError: null,

  // Academic Year
  academicYearOptions: [],
  academicYearLoading: false,
  academicYearError: null,

  // Class
  classOptions: [],
  classLoading: false,
  classError: null,

  // Staff Class & Subjects
  staffClassAndSubjectOptions: [],
  staffClassAndSubjectLoading: false,
  staffClassAndSubjectError: null,

  // Section
  sectionOptions: [],
  sectionLoading: false,
  sectionError: null,

  // Country
  countryOptions: [],
  countryLoading: false,
  countryError: null,

  // State options (for father, mother, guardian)
  fatherStateOptions: [],
  motherStateOptions: [],
  guardianStateOptions: [],
  stateLoading: false,
  stateError: null,
};

const masterDataSlice = createSlice({
  name: "masterData",
  initialState,
  reducers: {
    clearSectionOptions: (state) => {
      state.sectionOptions = [];
    },
    clearStateOptions: (state, action) => {
      const stateType = action.payload;
      if (stateType === STATE_TYPES.FATHER) {
        state.fatherStateOptions = [];
      } else if (stateType === STATE_TYPES.MOTHER) {
        state.motherStateOptions = [];
      } else if (stateType === STATE_TYPES.GUARDIAN) {
        state.guardianStateOptions = [];
      }
    },
    clearAllStateOptions: (state) => {
      state.fatherStateOptions = [];
      state.motherStateOptions = [];
      state.guardianStateOptions = [];
    },
    clearAllMasterData: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Income
      .addCase(fetchIncomeOptions.pending, (state) => {
        state.incomeLoading = true;
        state.incomeError = null;
      })
      .addCase(fetchIncomeOptions.fulfilled, (state, action) => {
        state.incomeLoading = false;
        state.incomeOptions = action.payload;
      })
      .addCase(fetchIncomeOptions.rejected, (state, action) => {
        state.incomeLoading = false;
        state.incomeError = action.payload;
      })

      // Occupation
      .addCase(fetchOccupationOptions.pending, (state) => {
        state.occupationLoading = true;
        state.occupationError = null;
      })
      .addCase(fetchOccupationOptions.fulfilled, (state, action) => {
        state.occupationLoading = false;
        state.occupationOptions = action.payload;
      })
      .addCase(fetchOccupationOptions.rejected, (state, action) => {
        state.occupationLoading = false;
        state.occupationError = action.payload;
      })

      // Blood Group
      .addCase(fetchBloodGroupOptions.pending, (state) => {
        state.bloodGroupLoading = true;
        state.bloodGroupError = null;
      })
      .addCase(fetchBloodGroupOptions.fulfilled, (state, action) => {
        state.bloodGroupLoading = false;
        state.bloodGroupOptions = action.payload;
      })
      .addCase(fetchBloodGroupOptions.rejected, (state, action) => {
        state.bloodGroupLoading = false;
        state.bloodGroupError = action.payload;
      })

      // Category/Caste
      .addCase(fetchCategoryOptions.pending, (state) => {
        state.categoryLoading = true;
        state.categoryError = null;
      })
      .addCase(fetchCategoryOptions.fulfilled, (state, action) => {
        state.categoryLoading = false;
        state.categoryOptions = action.payload;
      })
      .addCase(fetchCategoryOptions.rejected, (state, action) => {
        state.categoryLoading = false;
        state.categoryError = action.payload;
      })

      // Religion
      .addCase(fetchReligionOptions.pending, (state) => {
        state.religionLoading = true;
        state.religionError = null;
      })
      .addCase(fetchReligionOptions.fulfilled, (state, action) => {
        state.religionLoading = false;
        state.religionOptions = action.payload;
      })
      .addCase(fetchReligionOptions.rejected, (state, action) => {
        state.religionLoading = false;
        state.religionError = action.payload;
      })

      // Relation
      .addCase(fetchRelationOptions.pending, (state) => {
        state.relationLoading = true;
        state.relationError = null;
      })
      .addCase(fetchRelationOptions.fulfilled, (state, action) => {
        state.relationLoading = false;
        state.relationOptions = action.payload;
      })
      .addCase(fetchRelationOptions.rejected, (state, action) => {
        state.relationLoading = false;
        state.relationError = action.payload;
      })

      // Leave Reason
      .addCase(fetchLeaveReasonOptions.pending, (state) => {
        state.leaveReasonLoading = true;
        state.leaveReasonError = null;
      })
      .addCase(fetchLeaveReasonOptions.fulfilled, (state, action) => {
        state.leaveReasonLoading = false;
        state.leaveReasonOptions = action.payload;
      })
      .addCase(fetchLeaveReasonOptions.rejected, (state, action) => {
        state.leaveReasonLoading = false;
        state.leaveReasonError = action.payload;
      })

      // Academic Year
      .addCase(fetchAcademicYearOptions.pending, (state) => {
        state.academicYearLoading = true;
        state.academicYearError = null;
      })
      .addCase(fetchAcademicYearOptions.fulfilled, (state, action) => {
        state.academicYearLoading = false;
        state.academicYearOptions = action.payload;
      })
      .addCase(fetchAcademicYearOptions.rejected, (state, action) => {
        state.academicYearLoading = false;
        state.academicYearError = action.payload;
      })

      // Class
      .addCase(fetchClassOptions.pending, (state) => {
        state.classLoading = true;
        state.classError = null;
      })
      .addCase(fetchClassOptions.fulfilled, (state, action) => {
        state.classLoading = false;
        state.classOptions = action.payload;
      })
      .addCase(fetchClassOptions.rejected, (state, action) => {
        state.classLoading = false;
        state.classError = action.payload;
      })

      // Staff Subjects & Class
      .addCase(fetchStaffClassAndSubjects.pending, (state) => {
        state.staffClassAndSubjectLoading = true;
        state.staffClassAndSubjectError = null;
      })
      .addCase(fetchStaffClassAndSubjects.fulfilled, (state, action) => {
        state.staffClassAndSubjectLoading = false;
        state.staffClassAndSubjectOptions = action.payload;
      })
      .addCase(fetchStaffClassAndSubjects.rejected, (state, action) => {
        state.staffClassAndSubjectLoading = false;
        state.staffClassAndSubjectError = action.payload;
      })

      // Section
      .addCase(fetchSectionOptions.pending, (state) => {
        state.sectionLoading = true;
        state.sectionError = null;
      })
      .addCase(fetchSectionOptions.fulfilled, (state, action) => {
        state.sectionLoading = false;
        state.sectionOptions = action.payload;
      })
      .addCase(fetchSectionOptions.rejected, (state, action) => {
        state.sectionLoading = false;
        state.sectionError = action.payload;
      })

      // Country
      .addCase(fetchCountryOptions.pending, (state) => {
        state.countryLoading = true;
        state.countryError = null;
      })
      .addCase(fetchCountryOptions.fulfilled, (state, action) => {
        state.countryLoading = false;
        state.countryOptions = action.payload;
      })
      .addCase(fetchCountryOptions.rejected, (state, action) => {
        state.countryLoading = false;
        state.countryError = action.payload;
      })

      // State options
      .addCase(fetchStateOptions.pending, (state) => {
        state.stateLoading = true;
        state.stateError = null;
      })
      .addCase(fetchStateOptions.fulfilled, (state, action) => {
        state.stateLoading = false;
        const { states, stateType } = action.payload;

        if (stateType === STATE_TYPES.FATHER) {
          state.fatherStateOptions = states;
        } else if (stateType === STATE_TYPES.MOTHER) {
          state.motherStateOptions = states;
        } else if (stateType === STATE_TYPES.GUARDIAN) {
          state.guardianStateOptions = states;
        }
      })
      .addCase(fetchStateOptions.rejected, (state, action) => {
        state.stateLoading = false;
        state.stateError = action.payload?.message || "Failed to fetch states";
      });
  },
});

export const {
  clearSectionOptions,
  clearStateOptions,
  clearAllStateOptions,
  clearAllMasterData,
} = masterDataSlice.actions;

export default masterDataSlice.reducer;
